/**
 * Guest order documents request form (PP-933).
 *
 * Posts email + order number to the custom app's lookup endpoint via app
 * proxy. The endpoint always answers with the same neutral 200 whether or
 * not the data matched an order, so the success state simply tells the
 * visitor to check their inbox. Only transport-level failures (endpoint
 * missing, network down) surface the error message.
 *
 * The submit button ships disabled in the markup and is enabled here, so a
 * visitor without (working) JavaScript can never trigger a native submit.
 */

const ENDPOINT_SUFFIX = '/order-documents-request';
const REQUEST_TIMEOUT_MS = 15000;

class GhOrderDocumentsRequest extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  #submitting = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;
    this.#form.addEventListener('submit', this.#onSubmit);
    this.querySelector('[data-role="reset"]')?.addEventListener('click', this.#onReset);
    this.querySelector('.gh-order-docs__submit')?.removeAttribute('disabled');
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.querySelector('[data-role="reset"]')?.removeEventListener('click', this.#onReset);
  }

  /**
   * @param {string | null | undefined} proxyPath
   * @returns {string}
   */
  static buildEndpoint(proxyPath) {
    let raw =
      String(proxyPath ?? '')
        .trim()
        .replace(/\\/g, '') || '/apps/gastrohero';
    if (!raw.startsWith('/')) raw = `/${raw}`;
    raw = raw.replace(/\/+$/, '');
    return `${raw}${ENDPOINT_SUFFIX}`;
  }

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    this.#hideMessage('error');
    if (!this.#validate()) return;

    this.#send();
  };

  #validate() {
    let valid = true;

    const email = this.#input('email');
    const emailValid = Boolean(email && email.value.trim() !== '' && email.checkValidity());
    this.#toggleFieldError(email, 'email', !emailValid);
    if (!emailValid) valid = false;

    const orderNumber = this.#input('order_number');
    const orderNumberValid = Boolean(orderNumber && orderNumber.value.trim() !== '');
    this.#toggleFieldError(orderNumber, 'order_number', !orderNumberValid);
    if (!orderNumberValid) valid = false;

    if (!valid) {
      const firstInvalid = this.querySelector('input[aria-invalid="true"]');
      if (firstInvalid instanceof HTMLElement) firstInvalid.focus();
    }

    return valid;
  }

  async #send() {
    const button = this.querySelector('.gh-order-docs__submit');
    this.#submitting = true;
    button?.setAttribute('aria-busy', 'true');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(
        GhOrderDocumentsRequest.buildEndpoint(this.getAttribute('data-app-proxy-path')),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            email: this.#input('email')?.value.trim() ?? '',
            order_number: this.#input('order_number')?.value.trim() ?? '',
            locale: this.getAttribute('data-locale') || '',
            gh_verify: this.#input('gh_verify')?.value ?? '',
          }),
          signal: controller.signal,
        }
      );

      if (!response.ok) throw new Error(`Lookup endpoint answered ${response.status}`);

      this.#showSuccess();
    } catch {
      this.#showMessage('error');
    } finally {
      clearTimeout(timeout);
      this.#submitting = false;
      button?.removeAttribute('aria-busy');
    }
  }

  #showSuccess() {
    this.classList.add('gh-order-docs--done');
    if (this.#form) this.#form.hidden = true;
    const success = this.#showMessage('success');
    success?.focus();
  }

  /**
   * "Request more documents": back to an empty form for another lookup. The
   * previous values and any field errors are cleared so the visitor does not
   * resubmit the same request by accident.
   */
  #onReset = () => {
    this.classList.remove('gh-order-docs--done');
    this.#hideMessage('success');
    this.#hideMessage('error');
    this.#form?.reset();
    this.#toggleFieldError(this.#input('email'), 'email', false);
    this.#toggleFieldError(this.#input('order_number'), 'order_number', false);
    if (this.#form) this.#form.hidden = false;
    this.#input('email')?.focus();
  };

  /**
   * @param {string} role
   * @returns {HTMLElement | null}
   */
  #showMessage(role) {
    const message = this.querySelector(`[data-role="${role}"]`);
    if (!(message instanceof HTMLElement)) return null;
    message.hidden = false;
    return message;
  }

  /**
   * @param {string} role
   */
  #hideMessage(role) {
    const message = this.querySelector(`[data-role="${role}"]`);
    if (message instanceof HTMLElement) message.hidden = true;
  }

  /**
   * @param {string} name
   * @returns {HTMLInputElement | null}
   */
  #input(name) {
    const input = this.querySelector(`input[name="${name}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Toggles the visible field error and keeps aria-describedby consistent:
   * the static hint (data-describedby-base) always stays referenced, the
   * error id is appended only while the error is shown.
   *
   * @param {HTMLInputElement | null} input
   * @param {string} name
   * @param {boolean} showError
   */
  #toggleFieldError(input, name, showError) {
    const error = this.querySelector(`[data-error-for="${name}"]`);
    if (error instanceof HTMLElement) error.hidden = !showError;
    if (!input) return;

    const base = input.getAttribute('data-describedby-base') || '';
    const errorId = error instanceof HTMLElement ? error.id : '';

    if (showError) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }

    // Error first: matches the visual order (error sits directly under the input)
    const describedBy = [showError ? errorId : '', base].filter(Boolean).join(' ');
    if (describedBy) {
      input.setAttribute('aria-describedby', describedBy);
    } else {
      input.removeAttribute('aria-describedby');
    }
  }
}

if (!customElements.get('gh-order-documents-request')) {
  customElements.define('gh-order-documents-request', GhOrderDocumentsRequest);
}
